import { Component, type ErrorInfo, type ReactNode } from 'react';
import { EmptyState } from './EmptyState';
import { SupportLink } from './SupportLink';

export interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  /** Recover a failed view on navigation without remounting healthy layouts. */
  resetKey?: string;
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.props.onError?.(error, info);
  }

  componentDidUpdate(previous: ErrorBoundaryProps) {
    if (this.state.error && previous.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback) return this.props.fallback;
    return (
      <div style={{ padding: 32 }}>
        <EmptyState
          tone="attention"
          title="Something went wrong."
          body="Zoe hit an unexpected UI error. Refresh the page, or contact support if it repeats."
          action={
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <button type="button" className="v-btn" onClick={() => this.setState({ error: null })}>
                Try again
              </button>
              <SupportLink
                subject="Zoe UI error"
                context={{
                  errorName: this.state.error.name,
                  errorMessage: this.state.error.message,
                }}
              />
            </div>
          }
        />
      </div>
    );
  }
}
